import {DocumentNode, useMutation} from '@apollo/client'
import {Link, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import {Fragment, ReactNode, useCallback, useMemo, useState} from 'react'
import {defaultExporter, useRefresh, useTranslate} from 'react-admin'

import FileUploadButton from '../../components/FileUploadButton'
import {WarningIcon} from '../../components/icons'
import ProgressDialog from '../../components/ProgressDialog'
import RichTranslationText from '../../components/RichTranslationText'
import useIsDesktop from '../../hooks/useIsDesktop'

const ImportButton = ({
  buildVariables,
  children,
  className,
  exportInvalidRows,
  labels,
  mimeTypes = [
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
  ],
  mutation,
  onCompleted,
  onError,
  parseResult,
  refreshOnClose = true,
}: ImportButtonProps) => {
  const translate = useTranslate()
  const refresh = useRefresh()
  const isDesktop = useIsDesktop()
  const styles = useStyles()
  const [isProgressDialogOpen, setIsProgressDialogOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState<ReactNode | undefined>(undefined)
  const [runImport, {loading: isImporting}] = useMutation(mutation, {
    onCompleted: data => {
      const {
        failureReasons = [],
        invalidRows = [],
        invalidRowsCount = 0,
        successCount = 0,
      } = parseResult(data) || {}
      const exportInvalid = (rows: any[]) => {
        const transformed = exportInvalidRows?.transform ?
          exportInvalidRows.transform(rows) :
          rows
        defaultExporter(
          transformed,
          null as any,
          null as any,
          exportInvalidRows?.fileName || 'invalid_rows'
        )
      }
      setStatusMessage(!!invalidRowsCount ? (
        <div className={styles.merchantCustomerUsersImportStatusContainer}>
          <Typography>
            {translate('components.importButton.status.ignored', {invalidRowsCount})}
          </Typography>
          <div className={styles.merchantCustomerUsersImportFailureReasons}>
            {failureReasons?.map(r => (
              <Typography key={r} variant="body2">
                <WarningIcon color="warning" size="inherit"/>
                {labels?.failureReason?.(r) ?? r}
              </Typography>
            ))}
          </div>
          <Typography>
            <RichTranslationText
              rootElement={Fragment}
              tags={{
                link: match => (
                  <Link onClick={() => exportInvalid(invalidRows)}>{match}</Link>
                ),
              }}
              // todo: generify
              translationKey="components.importButton.exportInvalidRows"
            />
          </Typography>
        </div>
      ) : (
        <Typography>
          {labels?.success?.(successCount) ??
            `Imported ${successCount} row(s) successfully.`
          }
        </Typography>
      ))
      onCompleted?.(data)
    },
    onError: e => {
      setStatusMessage(
        labels?.error?.(e) ?? (
          <Typography>
            <WarningIcon color="error" size="inherit" />
            {e.message}
          </Typography>
        )
      )
      onError?.(e as Error)
    },
  })
  const closeProgressDialog = useCallback(() => {
    setStatusMessage(undefined)
    setIsProgressDialogOpen(false)
    if (refreshOnClose) refresh()
  }, [refresh, refreshOnClose])
  const onSelectFiles = useCallback(
    ([file]: FileAttachment[]) => {
      if (window.confirm(
        labels?.confirm?.(file.filename) ??
        `Import file "${file.filename}"?`
      )) {
        setIsProgressDialogOpen(true)
        const variables = buildVariables({file})
        runImport({variables})
      }
    },
    [buildVariables, runImport, labels]
  )
  const progressTitle = useMemo(
    () => labels?.progressTitle ?? 'Import in progress',
    [labels]
  )
  const progressingMessage = useMemo(
    () => labels?.progressMessage ?? 'Please wait while we process your file…',
    [labels]
  )
  return (
    <>
      <FileUploadButton
        className={className}
        loading={isImporting}
        mimeTypes={mimeTypes}
        onSelectFiles={onSelectFiles}
      >
        {children ?? (isDesktop && 'Import')}
      </FileUploadButton>
      <ProgressDialog
        isProgressing={isImporting}
        onClose={closeProgressDialog}
        open={isProgressDialogOpen}
        title={progressTitle}
      >
        {isImporting ? <Typography>{progressingMessage}</Typography> : statusMessage}
      </ProgressDialog>
    </>
  )
}

const useStyles = makeStyles(theme => ({
  merchantCustomerUserImportButton: {
    '& .MuiButton-label > .MuiSvgIcon-root:not(:last-child)': {
      marginRight: 0,
    },
  },
  merchantCustomerUsersImportFailureReasons: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.remSpacing(1),
  },
  merchantCustomerUsersImportStatusContainer: {
    '& .MuiTypography-root > .MuiLink-root': {
      verticalAlign: 'unset',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.remSpacing(2),
  },
}))

interface ImportExtractionResult {
  successCount?: number
  invalidRows?: any[]
  invalidRowsCount?: number
  failureReasons?: string[]
}

interface BuildVariablesArgs {
  file: FileAttachment
}

interface ImportButtonProps {
  // The GraphQL mutation that performs the import
  mutation: DocumentNode
  // Build mutation variables from the selected file
  buildVariables: (args: BuildVariablesArgs) => Record<string, any>
  // Extract normalized result from the mutation response
  parseResult: (data: any) => ImportExtractionResult
  // Export options for invalid rows
  exportInvalidRows?: {
    transform?: (rows: any[]) => any[]
    fileName: string
  }
  // Allowed MIME types for the file picker
  mimeTypes?: string[]
  // Optional: content for the FileUploadButton
  children?: ReactNode
  // Optional labels/messages (keep simple; i18n can be wired later)
  labels?: {
    confirm?: (filename: string) => string
    progressTitle?: string
    progressMessage?: string
    success?: (successCount?: number) => ReactNode
    error?: (e: Error) => ReactNode
    failureReason?: (reason: string) => ReactNode
  }
  // Optional side-effects
  onCompleted?: (data: any) => void
  onError?: (e: Error) => void
  // Whether to trigger refresh on dialog close (default: true)
  refreshOnClose?: boolean
  className?: string
}

export default ImportButton
